import { Canvas, type ThreeEvent } from "@react-three/fiber";
import {
  Bounds,
  OrbitControls,
  useBounds,
  useGLTF,
  useProgress,
  Html,
} from "@react-three/drei";
import {
  Suspense,
  useEffect,
  useRef,
  useState,
  type RefObject,
} from "react";
import * as THREE from "three";
import { useViewerStore } from "./store";
import {
  buildFaceColorAttribute,
  buildToleranceColorAttribute,
  deltaTypicalRange,
  fetchDeltaData,
  fetchLowEmissivitySet,
  fetchMeshMeta,
  fetchThermalData,
  fetchTruthResidual,
  summarizeTolerance,
  type MeshMetaInfo,
  type ThermalData,
} from "./thermal";
import { projectedToLatLon } from "./crs";

interface DisplayBundle {
  values: Float32Array;
  /** Slider bound min = palette anchor min. Typical-data low (p01). */
  rangeMin: number;
  /** Slider bound max = palette anchor max. Typical-data high (p99). */
  rangeMax: number;
  validCount: number;
  mode: "absolute" | "relative";
  radiusM?: number;
}

interface MeshProps {
  url: string;
  base: string;
}

/** Pass label from the bundle base path, e.g.
 *  ".../oil_battery__20260427/web/W1/" → "W1". Falls back to "—" if
 *  the URL doesn't fit the expected shape. */
function passLabelFromBase(base: string): string {
  const trimmed = base.replace(/\/$/, "");
  const last = trimmed.split("/").pop();
  return last && last.length > 0 ? last : "—";
}

interface BaseMeshState {
  /** The base mesh inside the loaded GLB. We share its BufferGeometry
   *  with a sibling overlay mesh so the thermal layer renders the
   *  same triangles without duplicating positions. */
  mesh: THREE.Mesh;
  /** World matrix at the moment the base mesh was prepared — copied
   *  onto the overlay so the two render perfectly coplanar. */
  worldMatrix: THREE.Matrix4;
}

// Loads + renders the bundle's mesh.glb. Two coplanar passes share
// one BufferGeometry:
//
//   1. Base mesh — the GLB itself, rendered via R3F's <primitive>.
//      Material gets swapped from PBR to MeshBasicMaterial (photo-
//      grammetry texture has lighting baked in; PBR re-shades and
//      darkens). Geometry gets de-indexed because trimesh's GLB
//      export keeps shared vertices, but per-face vertex coloring
//      requires each face to own its 3 vertices.
//
//   2. Thermal overlay — a sibling <mesh> in the R3F tree, with
//      vertexColors=true (RGBA). RGB carries the Lava palette, A
//      carries a "has thermal coverage" flag (0 for NaN faces) so
//      uncovered surfaces stay invisible regardless of opacity.
//      Sibling-not-child to avoid scene-graph cycles with
//      <primitive>.
function MeshFromUrl({ url, base }: MeshProps) {
  const { scene } = useGLTF(url);
  const setThermalSummary = useViewerStore((s) => s.setThermalSummary);
  const setSelectedFace = useViewerStore((s) => s.setSelectedFace);
  const thermalOn = useViewerStore((s) => s.thermalOn);
  const thermalOpacity = useViewerStore((s) => s.thermalOpacity);

  const [baseState, setBaseState] = useState<BaseMeshState | null>(null);
  const [thermalData, setThermalData] = useState<ThermalData | null>(null);
  const [meshMeta, setMeshMeta] = useState<MeshMetaInfo | null>(null);
  const [lowESet, setLowESet] = useState<Set<number>>(() => new Set());
  const [residuals, setResiduals] = useState<Float32Array | null>(null);
  const [toleranceColorAttr, setToleranceColorAttr] =
    useState<THREE.BufferAttribute | null>(null);
  const [display, setDisplay] = useState<DisplayBundle | null>(null);
  const viewMode = useViewerStore((s) => s.viewMode);
  const scaleRadius = useViewerStore((s) => s.scaleRadius);
  const bandLo = useViewerStore((s) => s.bandLo);
  const bandHi = useViewerStore((s) => s.bandHi);
  const setBand = useViewerStore((s) => s.setBand);
  const toleranceOn = useViewerStore((s) => s.toleranceOn);
  const toleranceOpacity = useViewerStore((s) => s.toleranceOpacity);
  const toleranceThreshold = useViewerStore((s) => s.toleranceThreshold);
  const setToleranceSummary = useViewerStore((s) => s.setToleranceSummary);
  const setToleranceAvailable = useViewerStore((s) => s.setToleranceAvailable);

  const passLabel = passLabelFromBase(base);

  // Tracks the previous view-mode so the load effect can tell mode-
  // entry (full reset) from a scale change within the same mode
  // (preserve direction). Without this every scale tick would also
  // call setBand(0, 0), wiping the user's chosen direction and
  // collapsing the panel back to "Pick a direction".
  const prevViewModeRef = useRef<typeof viewMode | null>(null);

  // Step 1: walk the scene, de-index + material-swap each mesh,
  // capture the first one as the "base" we'll color thermally.
  useEffect(() => {
    scene.updateMatrixWorld(true);
    let found: BaseMeshState | null = null;
    scene.traverse((obj) => {
      const mesh = obj as THREE.Mesh;
      if (!mesh.isMesh) return;

      if (mesh.geometry.index !== null) {
        const flat = mesh.geometry.toNonIndexed();
        mesh.geometry.dispose();
        mesh.geometry = flat;
      }

      const orig = mesh.material as THREE.MeshStandardMaterial | undefined;
      if (orig?.map) {
        orig.map.colorSpace = THREE.SRGBColorSpace;
        mesh.material = new THREE.MeshBasicMaterial({
          map: orig.map,
          side: THREE.FrontSide,
        });
      }

      if (!found) {
        found = { mesh, worldMatrix: mesh.matrixWorld.clone() };
      }
    });
    setBaseState(found);
  }, [scene]);

  // Step 2: fetch face_temp.f32 + face_meta.json + mesh_meta.json +
  // (optional) low_e_faces.u32 once per bundle. rawMeta drives
  // delta lookups in step 3; mesh_meta drives UTM conversion in the
  // inspect popup; low_e_faces is the operator-painted metal flag.
  useEffect(() => {
    if (!baseState) return;
    let cancelled = false;
    setThermalData(null);
    setMeshMeta(null);
    setLowESet(new Set());
    setDisplay(null);
    setSelectedFace(null);

    fetchThermalData(base)
      .then((data) => {
        if (cancelled) return;
        const expected = data.faceCount * 3;
        const haveVerts = baseState.mesh.geometry.attributes.position.count;
        if (haveVerts !== expected) {
          console.warn(
            `Vertex count mismatch: have ${haveVerts}, expected ${expected} ` +
              `(3 × ${data.faceCount} faces). Thermal layer disabled.`
          );
          return;
        }
        setThermalData(data);
      })
      .catch((err) => {
        console.warn("Thermal data load failed:", err);
      });

    fetchMeshMeta(base)
      .then((m) => {
        if (!cancelled) setMeshMeta(m);
      })
      .catch((err) => {
        console.warn("mesh_meta load failed:", err);
      });

    fetchLowEmissivitySet(base).then((s) => {
      if (!cancelled) setLowESet(s);
    });

    fetchTruthResidual(base).then((arr) => {
      if (cancelled) return;
      setResiduals(arr);
      setToleranceAvailable(arr !== null);
    });

    return () => {
      cancelled = true;
    };
  }, [baseState, base, setSelectedFace, setToleranceAvailable]);

  // Tolerance overlay attribute — recomputed when threshold changes.
  // Cheap (single pass over the residual array). Held in component
  // state because the overlay mesh shares the base geometry but
  // needs its OWN color attribute (separate from the thermal one).
  useEffect(() => {
    if (!residuals) {
      setToleranceColorAttr(null);
      setToleranceSummary(null);
      return;
    }
    const attr = buildToleranceColorAttribute(residuals, toleranceThreshold);
    setToleranceColorAttr(attr);
    setToleranceSummary(summarizeTolerance(residuals, toleranceThreshold));
  }, [residuals, toleranceThreshold, setToleranceSummary]);

  // Step 3: produce the display bundle for the active mode + scale.
  //   Absolute: face_temp directly. No smoothing — displayed values
  //     are exactly what was captured per face. Range = full data
  //     extent so toggle-on shows everything coloured.
  //   Relative: face_delta_r{NNN}.f32 at the chosen scale. Range is
  //     symmetric around 0 so cold and hot deltas read evenly.
  //
  // When this runs, also reset the active filter band to the full
  // range — the user's filter intent restarts per mode/scale.
  useEffect(() => {
    if (!baseState || !thermalData) return;
    let cancelled = false;

    const isModeEntry = prevViewModeRef.current !== viewMode;
    prevViewModeRef.current = viewMode;

    if (viewMode === "absolute") {
      // Slider bounds = palette anchor = typical range (p01..p99).
      // Default band = full slider extent on mode entry. Within
      // Absolute the scale slider isn't exposed so this branch only
      // re-runs on mode toggle.
      const lo = thermalData.rangeMin;
      const hi = thermalData.rangeMax;
      setDisplay({
        values: thermalData.temp,
        rangeMin: lo,
        rangeMax: hi,
        validCount: thermalData.validCount,
        mode: "absolute",
      });
      if (isModeEntry) setBand(lo, hi);
    } else {
      fetchDeltaData(base, scaleRadius, thermalData.rawMeta)
        .then((d) => {
          if (cancelled) return;
          const typical = deltaTypicalRange(thermalData.rawMeta, d.radiusM);
          setDisplay({
            values: d.values,
            rangeMin: typical.lo,
            rangeMax: typical.hi,
            validCount: d.validCount,
            mode: "relative",
            radiusM: d.radiusM,
          });

          if (isModeEntry) {
            // Mode entry — band parks at 0/0, no direction picked.
            // The user explicitly chooses cooler or warmer from the
            // panel buttons.
            setBand(0, 0);
          } else {
            // Scale change within Relative — preserve the user's
            // direction (cooler/warmer/none) but refit the band to
            // the new typical range so the slider always shows
            // something useful.
            const { bandLo: prevLo, bandHi: prevHi } =
              useViewerStore.getState();
            if (prevHi > 0) setBand(0, typical.hi);
            else if (prevLo < 0) setBand(typical.lo, 0);
            // direction === null → leave 0/0 untouched
          }
        })
        .catch((err) => {
          console.warn(`Display data load failed for r=${scaleRadius}m:`, err);
        });
    }

    return () => {
      cancelled = true;
    };
  }, [baseState, base, thermalData, viewMode, scaleRadius, setBand]);

  // Step 4: rebuild the colour attribute whenever the display bundle
  // or the band changes. Palette anchors = display.rangeMin/rangeMax
  // (= slider bounds, fixed). Band filters alpha — surfaces outside
  // band fall through to RGB. Outliers outside palette anchors also
  // fall through (band can't extend past palette without an explicit
  // outlier-toggle, which we don't ship in MVP).
  useEffect(() => {
    if (!baseState || !display) return;
    const colorAttr = buildFaceColorAttribute(
      display.values,
      display.rangeMin,
      display.rangeMax,
      bandLo,
      bandHi
    );
    baseState.mesh.geometry.setAttribute("color", colorAttr);
    setThermalSummary({
      rangeMin: display.rangeMin,
      rangeMax: display.rangeMax,
      mode: display.mode,
      radiusM: display.radiusM,
      validCount: display.validCount,
      faceCount: display.values.length,
    });
  }, [baseState, display, bandLo, bandHi, setThermalSummary]);

  // Click → identify the hit face, look up its data, transform the
  // hit point back into UTM, push a SelectedFace into the store.
  // The right panel reads from there.
  const handleClick = (event: ThreeEvent<MouseEvent>) => {
    if (!thermalData) return;
    event.stopPropagation();

    const faceId = event.faceIndex;
    if (
      faceId === undefined ||
      faceId === null ||
      faceId < 0 ||
      faceId >= thermalData.faceCount
    ) {
      return;
    }

    const t = thermalData.temp[faceId];
    const apparentTempC = Number.isFinite(t) ? t : null;

    const px = event.point.x;
    const py = event.point.y;
    const pz = event.point.z;

    // The GLB was built with translate(-center) + rotate(-90° X) on
    // the original Z-up vertices. To recover UTM:
    //   1. Inverse the Y-up→Z-up rotation: (x, y, z) → (x, -z, y)
    //   2. Add utm_center back.
    let utm: [number, number, number] | null = null;
    let latLon: [number, number] | null = null;
    let elevationM: number | null = null;
    if (meshMeta?.utm_center) {
      const [cx, cy, cz] = meshMeta.utm_center;
      utm = [px + cx, -pz + cy, py + cz];
      latLon = projectedToLatLon(utm);
      elevationM = utm[2];
    }

    setSelectedFace({
      faceId,
      passLabel,
      apparentTempC,
      utm,
      latLon,
      elevationM,
      worldPoint: [px, py, pz],
      isLowEmissivity: lowESet.has(faceId),
    });
  };

  return (
    <>
      <primitive object={scene} onClick={handleClick} />
      {baseState && display && (
        <ThermalOverlay
          geometry={baseState.mesh.geometry}
          worldMatrix={baseState.worldMatrix}
          visible={thermalOn && thermalOpacity > 0}
          opacity={thermalOpacity}
        />
      )}
      {baseState && toleranceColorAttr && (
        <ToleranceOverlay
          geometry={baseState.mesh.geometry}
          worldMatrix={baseState.worldMatrix}
          colorAttr={toleranceColorAttr}
          visible={toleranceOn && toleranceOpacity > 0}
          opacity={toleranceOpacity}
        />
      )}
      <SelectionMarker />
    </>
  );
}

interface ToleranceOverlayProps {
  geometry: THREE.BufferGeometry;
  worldMatrix: THREE.Matrix4;
  colorAttr: THREE.BufferAttribute;
  visible: boolean;
  opacity: number;
}

/** Sibling overlay carrying the tolerance bin colours. Identical
 *  pattern to ThermalOverlay but with its own color attribute (sage
 *  for within tolerance, copper for outside). renderOrder=2 so it
 *  draws ON TOP of the thermal overlay when both layers are on —
 *  tolerance is the trust signal and should win. */
function ToleranceOverlay({
  geometry,
  worldMatrix,
  colorAttr,
  visible,
  opacity,
}: ToleranceOverlayProps) {
  const [transform] = useState(() => {
    const pos = new THREE.Vector3();
    const rot = new THREE.Quaternion();
    const scl = new THREE.Vector3();
    worldMatrix.decompose(pos, rot, scl);
    return { pos, rot, scl };
  });

  // Need to apply the color attribute to the geometry for THIS mesh
  // — but the geometry is shared with the base + thermal overlay, so
  // we can't just setAttribute("color", ...) without overwriting the
  // thermal one. Solution: clone the geometry just enough to swap
  // attributes. But for simplicity, we use a separate attribute name
  // wired through a custom-shader material later. For now: render
  // an independent geometry that shares position/uv but has its own
  // color attribute.
  //
  // Even simpler: create a fresh BufferGeometry that references the
  // same positions but holds tolerance colors. Three.js shares
  // attribute buffers when you reuse the same BufferAttribute, so
  // memory cost is minimal.
  const [toleranceGeometry] = useState(() => {
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", geometry.getAttribute("position"));
    return g;
  });

  useEffect(() => {
    toleranceGeometry.setAttribute("color", colorAttr);
    return () => {
      // Don't dispose the shared position attribute; only release the
      // wrapper geometry on unmount.
    };
  }, [toleranceGeometry, colorAttr]);

  useEffect(() => {
    return () => {
      toleranceGeometry.dispose();
    };
  }, [toleranceGeometry]);

  return (
    <mesh
      geometry={toleranceGeometry}
      visible={visible}
      renderOrder={2}
      raycast={() => null}
      position={[transform.pos.x, transform.pos.y, transform.pos.z]}
      quaternion={[
        transform.rot.x,
        transform.rot.y,
        transform.rot.z,
        transform.rot.w,
      ]}
      scale={[transform.scl.x, transform.scl.y, transform.scl.z]}
    >
      <meshBasicMaterial
        vertexColors
        transparent
        opacity={opacity}
        depthWrite={false}
        side={THREE.FrontSide}
        polygonOffset
        polygonOffsetFactor={-2}
        polygonOffsetUnits={-2}
      />
    </mesh>
  );
}

/** Small copper sphere drawn at the currently-selected hit point so
 *  the user has a visual anchor for what they clicked. Drawn always-
 *  on-top via depthTest=false + renderOrder=10. */
function SelectionMarker() {
  const selected = useViewerStore((s) => s.selectedFace);
  if (!selected) return null;
  return (
    <mesh
      position={selected.worldPoint}
      renderOrder={10}
      raycast={() => null}
    >
      <sphereGeometry args={[0.12, 24, 24]} />
      <meshBasicMaterial color="#c47a2a" depthTest={false} />
    </mesh>
  );
}

interface ThermalOverlayProps {
  geometry: THREE.BufferGeometry;
  worldMatrix: THREE.Matrix4;
  visible: boolean;
  opacity: number;
}

// Sibling overlay mesh — declared in R3F so it lives in the same
// reconciler tree as <primitive>. Position/rotation/scale come from
// the base mesh's world matrix, decomposed once on mount.
function ThermalOverlay({
  geometry,
  worldMatrix,
  visible,
  opacity,
}: ThermalOverlayProps) {
  const [transform] = useState(() => {
    const pos = new THREE.Vector3();
    const rot = new THREE.Quaternion();
    const scl = new THREE.Vector3();
    worldMatrix.decompose(pos, rot, scl);
    return { pos, rot, scl };
  });

  return (
    <mesh
      geometry={geometry}
      visible={visible}
      renderOrder={1}
      raycast={() => null}
      position={[transform.pos.x, transform.pos.y, transform.pos.z]}
      quaternion={[
        transform.rot.x,
        transform.rot.y,
        transform.rot.z,
        transform.rot.w,
      ]}
      scale={[transform.scl.x, transform.scl.y, transform.scl.z]}
    >
      <meshBasicMaterial
        vertexColors
        transparent
        opacity={opacity}
        depthWrite={false}
        side={THREE.FrontSide}
        polygonOffset
        polygonOffsetFactor={-1}
        polygonOffsetUnits={-1}
      />
    </mesh>
  );
}

interface ResetExposerProps {
  resetRef: RefObject<() => void>;
}

function ResetExposer({ resetRef }: ResetExposerProps) {
  const bounds = useBounds();
  useEffect(() => {
    resetRef.current = () => {
      bounds.refresh().fit();
    };
  }, [bounds, resetRef]);
  return null;
}

function LoadProgress() {
  const { progress, active } = useProgress();
  if (!active) return null;
  return (
    <Html
      center
      style={{
        background: "rgba(255,255,255,0.85)",
        padding: "10px 18px",
        borderRadius: "8px",
        fontFamily: "var(--font-body)",
        fontSize: "12px",
        color: "var(--color-slate)",
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
      }}
    >
      Loading mesh… {progress.toFixed(0)}%
    </Html>
  );
}

interface ViewerProps {
  meshUrl: string;
  base: string;
  resetRef: RefObject<() => void>;
}

export function Viewer({ meshUrl, base, resetRef }: ViewerProps) {
  return (
    <Canvas
      camera={{ position: [10, 10, 10], fov: 38, near: 0.1, far: 5000 }}
      dpr={[1, 2]}
      gl={{
        antialias: true,
        preserveDrawingBuffer: true,
        toneMapping: THREE.NoToneMapping,
      }}
    >
      <color attach="background" args={["#f1f3f6"]} />
      <ambientLight intensity={0.6} />
      <directionalLight position={[10, 14, 8]} intensity={0.85} />

      <Suspense fallback={<LoadProgress />}>
        <Bounds fit clip observe margin={1.15}>
          <MeshFromUrl url={meshUrl} base={base} />
          <ResetExposer resetRef={resetRef} />
        </Bounds>
      </Suspense>

      <OrbitControls
        makeDefault
        enableDamping
        dampingFactor={0.08}
        screenSpacePanning
      />
    </Canvas>
  );
}
